import { authService } from './auth';
import { API_BASE, ApiError, downloadAuthorizedFile, request } from './core.api';

export interface PlaceDescriptionTemplateItemDto {
  id: string;
  label: string;
  sortOrder: number;
}

export interface PlaceDescriptionTemplateSectionDto {
  id: string;
  name: string;
  sortOrder: number;
  items: PlaceDescriptionTemplateItemDto[];
}

export interface PlaceDescriptionTemplateDto {
  id: string;
  name: string;
  description?: string;
  sections: PlaceDescriptionTemplateSectionDto[];
}

export interface PlaceDescriptionPhotoDto {
  id: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  createdAt: string;
}

export interface PlaceDescriptionItemDto {
  id: string;
  label: string;
  sortOrder: number;
  condition: string;
  comment?: string;
  photos: PlaceDescriptionPhotoDto[];
}

export interface PlaceDescriptionSectionDto {
  id: string;
  name: string;
  sortOrder: number;
  items: PlaceDescriptionItemDto[];
}

export interface PlaceDescriptionSummaryDto {
  id: string;
  tenantId: string;
  propertyId: string;
  propertyAddress: string;
  templateId: string;
  templateName: string;
  title: string;
  kind: string;
  inspectionDate: string;
  generalNotes?: string;
  sectionCount: number;
  itemCount: number;
  photoCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlaceDescriptionDetailDto {
  id: string;
  tenantId: string;
  tenantName: string;
  propertyId: string;
  propertyAddress: string;
  templateId: string;
  templateName: string;
  title: string;
  kind: string;
  inspectionDate: string;
  generalNotes?: string;
  sections: PlaceDescriptionSectionDto[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePlaceDescriptionRequest {
  propertyId: string;
  templateId: string;
  kind: string;
  inspectionDate: string;
  title?: string;
  generalNotes?: string;
}

export interface UpdatePlaceDescriptionItemRequest {
  id: string;
  condition: string;
  comment?: string;
}

export interface UpdatePlaceDescriptionSectionRequest {
  id: string;
  items: UpdatePlaceDescriptionItemRequest[];
}

export interface UpdatePlaceDescriptionRequest {
  title: string;
  inspectionDate: string;
  generalNotes?: string;
  sections: UpdatePlaceDescriptionSectionRequest[];
}

export interface CreatePlaceDescriptionPhotoRequest {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
}

export interface PlaceDescriptionPhotoUploadUrlDto {
  photoId: string;
  uploadUrl: string;
}

export interface PlaceDescriptionDownloadedFile {
  blob: Blob;
  fileName: string;
}

async function requestFile(path: string): Promise<PlaceDescriptionDownloadedFile> {
  const token = authService.accessToken;
  if (!token) {
    authService.logout();
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (response.status === 401) {
    authService.logout();
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(error.error || `HTTP ${response.status}`, response.status, error.code, error.details);
  }

  const blob = await response.blob();
  const header = response.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/i.exec(header);
  return { blob, fileName: match?.[1] || 'plaatsbeschrijving.pdf' };
}

export const placeDescriptionsApi = {
  getTemplates: () => request<PlaceDescriptionTemplateDto[]>('/place-description-templates'),
  getByTenant: (tenantId: string) => request<PlaceDescriptionSummaryDto[]>(`/tenants/${tenantId}/place-descriptions`),
  getById: (id: string) => request<PlaceDescriptionDetailDto>(`/place-descriptions/${id}`),
  create: (tenantId: string, data: CreatePlaceDescriptionRequest) =>
    request<PlaceDescriptionDetailDto>(`/tenants/${tenantId}/place-descriptions`, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdatePlaceDescriptionRequest) =>
    request<PlaceDescriptionDetailDto>(`/place-descriptions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/place-descriptions/${id}`, { method: 'DELETE' }),
  createPhotoUpload: (placeDescriptionId: string, itemId: string, data: CreatePlaceDescriptionPhotoRequest) =>
    request<PlaceDescriptionPhotoUploadUrlDto>(`/place-descriptions/${placeDescriptionId}/items/${itemId}/photos`, { method: 'POST', body: JSON.stringify(data) }),
  downloadPhoto: (photoId: string, fileName?: string) => downloadAuthorizedFile(`/place-description-photos/${photoId}/download`, fileName),
  deletePhoto: (photoId: string) => request<void>(`/place-description-photos/${photoId}`, { method: 'DELETE' }),
  export: (id: string) => requestFile(`/place-descriptions/${id}/export`),
};
